/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useState, useLayoutEffect, type PropsWithChildren } from 'react';
import { type PreloadedQuery, graphql, usePreloadedQuery } from 'react-relay';

import { JSErrorBoundary } from '@jira/platform__js-error-boundary/src/JSErrorBoundary.tsx';
import { PanelSplitter } from '@atlaskit/navigation-system/layout/panel-splitter';
import { RightPanel } from '@jira/design-system__navigation-system/src/PageLayout.tsx';
import { useCurrentPreviewPanel } from '@atlassian/preview-panels-api/use-current-preview-panel';
import { useIntl, defineMessage } from '@atlassian/jira-intl/src/index.tsx';
import { useRelayResource } from '@jira/platform__graphql-relay-utils/src/services/resources/index.tsx';
import { previewPanelStateResource } from '@jira/navigation-apps__resources/src/services/preview-panel/index.tsx';
import { useLayoutStoreActions } from '@jira/platform__controllers-layout/src/controllers/layout-controller/store/index.tsx';
import { functionWithCondition } from '@jira/platform__utils-feature-flagging-utils/src/utils/function-with-condition/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import { jsx, cssMap } from '@atlaskit/css';
import Placeholder from '@atlassian/jira-placeholder/src/index.tsx';
import { fireUIAnalytics, useAnalyticsEvents } from '@atlassian/jira-product-analytics-bridge';
import type { PreviewPanelQuery } from './__generated__/PreviewPanelQuery.graphql.ts';
import { useIssuePreviewPanelsNavigation } from './useIssuePreviewPanelsNavigation.tsx';

const previewPanelSplitterLabel = defineMessage({
	id: 'page-container-v2.common.preview-panel.preview-panel-splitter-label',
	defaultMessage: 'Resize preview panel',
	description: 'Button label for resizing the preview panel',
});

const styles = cssMap({
	clickCapture: {
		display: 'contents',
	},
});

const MaybeMarkPreviewPanelClick = functionWithCondition(
	() => fg('jivtech-1920-back-navigation-gpp'),
	({ children }: PropsWithChildren<{}>) => {
		const { markPreviewPanelClick } = useIssuePreviewPanelsNavigation();
		return (
			<div css={styles.clickCapture} onClickCapture={markPreviewPanelClick}>
				{children}
			</div>
		);
	},
	({ children }: PropsWithChildren<{}>) => <>{children}</>,
);

const PreviewPanelInternal = ({
	queryReference,
}: {
	queryReference: PreloadedQuery<PreviewPanelQuery>;
}) => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const [, { setPreviewPanelState }] = useLayoutStoreActions();
	const [useLocalWidth, setUseLocalWidth] = useState(false);
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);
			setPreviewPanelState({
				width: finalWidth,
			});
			setUseLocalWidth(true);

			fireUIAnalytics(createAnalyticsEvent({}), 'preview-panel resized', {
				panelId: panel?.id,
				panelType: panel?.type,
				initialWidth,
				finalWidth,
			});
		},
		[panel, setPreviewPanelState, createAnalyticsEvent],
	);

	const data = usePreloadedQuery<PreviewPanelQuery>(
		graphql`
			query PreviewPanelQuery($cloudId: ID!) {
				jira {
					navigationUIState(cloudId: $cloudId) {
						previewPanel {
							width
						}
					}
				}
			}
		`,
		queryReference,
	);

	useLayoutEffect(() => {
		const widthFromQuery = data?.jira?.navigationUIState?.previewPanel?.width ?? null;
		if (panel && !useLocalWidth && widthFromQuery != null) {
			panel.setWidth?.(widthFromQuery);
		}
	}, [panel, useLocalWidth, data?.jira?.navigationUIState?.previewPanel?.width]);

	if (panel === null) {
		return null;
	}

	const { content: PanelContent } = panel;

	const previewPanelWidth = useLocalWidth
		? panel?.width
		: (data?.jira?.navigationUIState?.previewPanel?.width ?? panel?.width);

	return (
		<RightPanel width={previewPanelWidth} testId="preview-panels.preview-panel">
			<MaybeMarkPreviewPanelClick>
				<PanelContent />
			</MaybeMarkPreviewPanelClick>
			<PanelSplitter
				label={formatMessage(previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

const PreviewPanelWithoutWidthPersistance = () => {
	const panel = useCurrentPreviewPanel();
	const { formatMessage } = useIntl();
	const { createAnalyticsEvent } = useAnalyticsEvents();

	const onResizeEnd = useCallback(
		({ initialWidth, finalWidth }: { initialWidth: number; finalWidth: number }) => {
			panel?.setWidth?.(finalWidth);

			fireUIAnalytics(createAnalyticsEvent({}), 'preview-panel resized', {
				panelId: panel?.id,
				panelType: panel?.type,
				initialWidth,
				finalWidth,
			});
		},
		[panel, createAnalyticsEvent],
	);

	if (panel === null) {
		return null;
	}

	const { content: PanelContent } = panel;

	const previewPanelWidth = panel?.width;

	return (
		<RightPanel width={previewPanelWidth} testId="preview-panels.preview-panel">
			<PanelContent />
			<PanelSplitter
				label={formatMessage(previewPanelSplitterLabel)}
				onResizeEnd={onResizeEnd}
				testId="preview-panels.panel-splitter"
			/>
		</RightPanel>
	);
};

const PreviewPanelFallback = () => <PreviewPanelWithoutWidthPersistance />;

export const PreviewPanel = (): React.JSX.Element | null => {
	const { queryReference } = useRelayResource(previewPanelStateResource);

	if (!queryReference) {
		return null;
	}

	return (
		<JSErrorBoundary
			id="preview-panel-width-query"
			packageName="preview-panels"
			teamName="Gryffindor"
			fallback={PreviewPanelFallback}
		>
			<Placeholder name="preview-panel-width-query" fallback={<PreviewPanelFallback />}>
				<PreviewPanelInternal queryReference={queryReference} />
			</Placeholder>
		</JSErrorBoundary>
	);
};
